import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Typography, Button, Upload, Alert, Progress,
  Table, Space, Row, Col, Statistic, Collapse, Tag, message, Modal, Spin, Select
} from 'antd';
import {
  UploadOutlined, ArrowLeftOutlined, FileExcelOutlined,
  DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  InboxOutlined, EyeOutlined, InfoCircleOutlined, LoadingOutlined, TeamOutlined
} from '@ant-design/icons';
import { cargaMasivaService, categoriaService, eventoService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

// Estimacion del tiempo de carga, medido sobre produccion (2026-08-12).
// El backend precarga catalogos y RPs (3 queries) y commitea por lotes, asi
// que el costo por fila es casi cero: lo que pesa es el arranque. Antes de ese
// fix cada fila costaba 4 round trips a Supabase (~1,5s) y 79 animales
// llenaban los 120s de gunicorn.
const estimarSegundosCarga = (filas) => {
  if (!filas) return null;
  const ARRANQUE = 3;      // 3 queries de precarga (~380ms c/u) + overhead
  const POR_FILA = 0.05;   // parseo en memoria + INSERT dentro del batch
  return Math.max(5, Math.ceil(ARRANQUE + filas * POR_FILA));
};

const formatTiempo = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return m > 0 ? `${m}m ${ss.toString().padStart(2, '0')}s` : `${ss}s`;
};

const CargaMasiva = () => {
  /* La MISMA pantalla en dos modos, y no dos pantallas.
   *
   * Con un remate elegido estamos cargando DENTRO de el: la columna CABANA del
   * CSV nombra la cabaña de cada animal (su codigo corto o su nombre, de las
   * invitadas o las mias) y el animal nace ya asignado al remate y a su dueño.
   * Sin remate es la carga de siempre, mi propia hacienda: la misma columna,
   * opcional, elige entre MIS cabañas, y con una sola el backend la asigna solo.
   *
   * EL REMATE SE ELIGE ACA, EN LA PANTALLA (2026-10-26). Hasta ese dia lo
   * decidia la URL: `/eventos/:id/carga-masiva` era modo remate y
   * `/animales/carga-masiva` no tenia forma de elegir uno. El productor entraba
   * por Animales, que es la puerta natural, bajaba la plantilla sin CABANA y no
   * entendia por que (usuario, con Monte Grande). La URL del remate sigue
   * funcionando como atajo: deja el remate preseleccionado.
   *
   * Dos pantallas habrian sido dos caminos de validacion, dos manejos de error
   * y dos lugares donde arreglar el proximo bug. */
  const { id: idRuta } = useParams();
  const navigate = useNavigate();
  const { tieneRecurso } = useAuth();
  const puedeMulti = tieneRecurso('multi_cabanas');
  const [idEvento, setIdEvento] = useState(idRuta ? Number(idRuta) : null);
  const [remates, setRemates] = useState([]);
  // id_evento -> invitaciones ACTIVAS de ese remate (con `invitada` y sus
  // `cabanas`), para marcar los remates compartidos en el selector y listar
  // que se escribe en CABANA. Solo quien organiza remates compartidos puede
  // pedirlas; para el resto queda vacio y el selector no marca nada.
  const [invitadasPorRemate, setInvitadasPorRemate] = useState({});
  const [cargandoRemates, setCargandoRemates] = useState(false);

  useEffect(() => {
    setIdEvento(idRuta ? Number(idRuta) : null);
  }, [idRuta]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setCargandoRemates(true);
      try {
        // Solo los remates en curso: cargar animales a uno terminado no tiene sentido.
        const res = await eventoService.getListaCorta({ estado: 1 });
        if (!cancelado) setRemates(eventosDeListaCorta(res));
      } catch (error) {
        if (!cancelado) message.error(error.response?.data?.error || 'No pudimos cargar tus remates');
      }
      if (puedeMulti) {
        try {
          const res = await eventoService.getInvitacionesEnviadas();
          const porRemate = {};
          (res.data?.invitaciones || []).filter((i) => i.activa).forEach((i) => {
            (porRemate[i.id_evento] = porRemate[i.id_evento] || []).push(i);
          });
          if (!cancelado) setInvitadasPorRemate(porRemate);
        } catch {
          // Sin esto la pantalla sigue sirviendo: solo falta la marca de
          // "multi-cabaña" en el selector.
        }
      }
      if (!cancelado) setCargandoRemates(false);
    })();
    return () => { cancelado = true; };
  }, [puedeMulti]);

  const remateElegido = remates.find((r) => r.id_evento === idEvento) || null;
  const invitadasDelRemate = (idEvento && invitadasPorRemate[idEvento]) || [];

  // Cambiar de remate invalida lo que se validó contra el anterior: la
  // columna CABANA se resuelve contra cabañas distintas.
  const elegirRemate = (valor) => {
    setIdEvento(valor || null);
    setFile(null);
    setFilasArchivo(null);
    setResultadosValidacion(null);
    setResultadosCarga(null);
  };
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [resultadosCarga, setResultadosCarga] = useState(null);
  const [resultadosValidacion, setResultadosValidacion] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [filasArchivo, setFilasArchivo] = useState(null);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);

  // Cronometro del modal: arranca con uploading y se resetea al terminar.
  // Solo informativo — la request sigue siendo sincrona.
  useEffect(() => {
    if (!uploading) {
      setTiempoTranscurrido(0);
      return;
    }
    const interval = setInterval(() => {
      setTiempoTranscurrido((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [uploading]);

  // Cuenta las filas del CSV en el navegador para poder estimar el tiempo
  // ANTES de mandar nada. Se descuenta el header y las lineas en blanco.
  const contarFilasCsv = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const lineas = String(e.target.result)
        .split(/\r?\n/)
        .filter((l) => l.trim() !== '');
      setFilasArchivo(Math.max(0, lineas.length - 1));
    };
    reader.onerror = () => setFilasArchivo(null);
    reader.readAsText(f);
  };

  const segundosEstimados = estimarSegundosCarga(filasArchivo);

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    setLoadingCategorias(true);
    try {
      const response = await categoriaService.getAll();
      const cats = Array.isArray(response.data) ? response.data : [];
      setCategorias(cats);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      // Sin este aviso el usuario ve el Alert de "no hay categorías" y cree
      // que la cabaña no tiene ninguna: termina inventando id_categoria en
      // el CSV. Un fallo mudo que genera datos malos.
      message.error(
        error.response?.data?.error ||
        'No se pudieron cargar las categorías. Recargá la página antes de armar el CSV.'
      );
    } finally {
      setLoadingCategorias(false);
    }
  };

  const handleDescargarPlantilla = async () => {
    try {
      // Dos plantillas y la diferencia importa. La DEL REMATE trae una fila de
      // ejemplo por cabaña invitada (y por cada una de las mias si tengo
      // varias) con lo que hay que escribir en CABANA: sin eso el productor
      // tendria que acordarse de como se llama cada una, y no se acuerda. La
      // suelta trae la columna SOLO si tengo mas de una cabaña activa (el
      // backend lo sabe por el token); con una, la cabaña se asigna sola.
      const response = idEvento
        ? await cargaMasivaService.descargarPlantillaRemate(idEvento)
        : await cargaMasivaService.descargarPlantilla();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download',
        idEvento ? 'plantilla_remate.csv' : 'animales_plantilla.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Plantilla descargada');
    } catch (error) {
      console.error('Error descarga:', error);
      // responseType blob: ante un 500 error.response.data es un Blob, no
      // JSON, asi que el fallback casi siempre gana. Se deja igual para no
      // ser el unico catch del archivo que ignora el mensaje del backend.
      message.error(error.response?.data?.error || 'Error al descargar la plantilla');
    }
  };

  const handleValidar = async () => {
    if (!file) {
      message.warning('Seleccione un archivo primero');
      return;
    }

    setValidating(true);
    setResultadosValidacion(null);

    try {
      // Con el mismo remate que la carga: validar sin el remate no puede
      // resolver la columna CABANA y rechazaria un archivo que carga bien.
      const response = await cargaMasivaService.validarCSV(file, idEvento);
      setResultadosValidacion(response.data);

      if (response.data?.total_errores === 0) {
        message.success('Archivo válido, puede proceder con la carga');
      } else {
        message.warning(`Se encontraron ${response.data?.total_errores} errores`);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al validar');
    } finally {
      setValidating(false);
    }
  };

  /* LOS QUE NACIERON SIN CABAÑA. El backend no frena la carga por eso
     —decision del usuario: un productor con 200 filas no para por un dato
     que completa en dos clicks— pero tampoco puede quedar en silencio: un
     animal sin cabaña NO entra al catalogo de genetica, y la trampa era
     justamente que nada lo decia. Solo pasa cuando el cliente tiene VARIAS
     cabañas y la fila no dijo cual; con una sola el backend la asigna solo y
     `sin_cabana` viene vacio.

     "Vincular" lleva al listado YA FILTRADO por los que no tienen cabaña:
     ahi estan los tildes y el "Asignar cabaña" de siempre. No se construyo
     otra pantalla para hacer lo que una existente ya hace. */
  const avisarSinCabana = (ids) => {
    if (!ids || ids.length === 0) return;
    Modal.confirm({
      title: `Cargaste ${ids.length} animal${ids.length === 1 ? '' : 'es'} sin decir a qué cabaña pertenece${ids.length === 1 ? '' : 'n'}`,
      content: (
        <Paragraph style={{ marginBottom: 0 }}>
          Sin cabaña, un animal <strong>no entra al catálogo de genética</strong>.
          Podés asignársela ahora, tildándolos en el listado, o dejarlo para después.
        </Paragraph>
      ),
      okText: 'Vincular con su cabaña',
      cancelText: 'OK, ya lo sé',
      onOk: () => navigate('/animales?sin_cabana=1'),
    });
  };

  const handleCargar = async () => {
    if (!file) {
      message.warning('Seleccione un archivo primero');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setResultadosCarga(null);

    try {
      const response = await cargaMasivaService.cargarAnimales(
        file,
        (progressEvent) => {
          // `total` es opcional en axios v1: sin la guarda queda NaN y el
          // Progress del modal desaparece (toda comparacion con NaN da false).
          const percent = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(percent);
        },
        // Con remate, cada animal nace con SU dueño (via el alias de la
        // columna CABANA) y ya asignado al evento.
        idEvento
      );

      setResultadosCarga(response.data);
      setFile(null);
      setFilasArchivo(null);

      if (response.data?.total_exitosos > 0) {
        message.success(`${response.data?.total_exitosos} animal(es) cargado(s) exitosamente`);
      }
      if (response.data?.total_errores > 0) {
        message.warning(`${response.data?.total_errores} registro(s) con errores`);
      }
      avisarSinCabana(response.data?.sin_cabana);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar');
    } finally {
      // OBLIGATORIO en finally: `uploading` es lo unico que cierra el modal de
      // progreso, que es closable={false} + maskClosable={false}. Si esto se
      // saltea por una excepcion, el usuario queda encerrado en un cartel que
      // le dice "no recargues la pestania" y su unica salida es recargarla.
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadProps = {
    maxCount: 1,
    accept: '.csv',
    fileList: file ? [file] : [],
    onRemove: () => {
      setFile(null);
      setResultadosValidacion(null);
      setFilasArchivo(null);
    },
    beforeUpload: (f) => {
      if (!f.name.endsWith('.csv')) {
        message.error('Solo se permiten archivos CSV');
        return false;
      }
      setFile(f);
      contarFilasCsv(f);
      setResultadosValidacion(null);
      setResultadosCarga(null);
      return false;
    },
  };

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(idRuta ? `/eventos/${idRuta}` : '/animales')}
        style={{ marginBottom: 16 }}
      >
        {idRuta ? 'Volver al remate' : 'Volver a Animales'}
      </Button>

      {/* Título y botón de descarga alineados */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <FileExcelOutlined /> Carga Masiva de Animales
          </Title>
        </Col>
        <Col>
          <Button 
            type="primary" 
            size="large"
            icon={<DownloadOutlined />}
            onClick={handleDescargarPlantilla}
          >
            Descargar Plantilla CSV
          </Button>
        </Col>
      </Row>

      {/* LA DECISION, ANTES QUE NADA: ¿para que es esta carga? Es lo que cambia
          la plantilla, la validacion y a quien quedan asignados los animales,
          asi que se toma aca y no segun por que puerta se entro. Los remates
          compartidos se marcan en la propia opcion —un boton aparte seria una
          tercera puerta— y el aviso de que los animales nacen asignados va en
          la misma card, porque desde aca el remate queda mas a mano que antes. */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]} align="middle">
          <Col xs={24} md={9}>
            <Text strong>¿Para qué es esta carga?</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Con un remate elegido, cada animal nace ya asignado a ese remate.
            </Text>
          </Col>
          <Col xs={24} md={15}>
            <Select
              style={{ width: '100%' }}
              value={idEvento || 0}
              onChange={elegirRemate}
              loading={cargandoRemates}
              disabled={uploading}
              options={[
                { value: 0, label: 'Mi hacienda, sin remate' },
                ...remates.map((r) => ({
                  value: r.id_evento,
                  label: invitadasPorRemate[r.id_evento] ? (
                    <Space size={6} wrap>
                      <span>{r.nombre_evento}</span>
                      <Tag color="purple" style={{ margin: 0 }}>
                        <TeamOutlined /> multi-cabaña · {invitadasPorRemate[r.id_evento].map((i) => i.invitada).join(', ')}
                      </Tag>
                    </Space>
                  ) : r.nombre_evento,
                })),
              ]}
            />
            {idRuta && !cargandoRemates && !remateElegido && (
              <Text type="warning" style={{ fontSize: 12 }}>
                Este remate no está en curso: la carga igual lo toma, pero revisá que sea el correcto.
              </Text>
            )}
          </Col>
        </Row>
      </Card>

      {/* Card con dos columnas */}
      {/* La explicacion de la columna CABANA va ARRIBA de todo y solo en modo
          remate. Es lo unico de esta pantalla que el productor no puede deducir
          mirando el archivo: que un texto en una columna decide de QUIEN es el
          animal. Lo que se escribe es el codigo corto de la CABAÑA (o su
          nombre), el mismo que muestra la pantalla de Cabañas: no hay un alias
          por invitacion. */}
      {idEvento && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Estás cargando animales dentro del remate «${remateElegido?.nombre_evento || idEvento}»`}
          description={
            <div>
              {invitadasDelRemate.length > 0 && (
                <Paragraph style={{ marginBottom: 8 }}>
                  <TeamOutlined /> Remate <strong>multi-cabaña</strong>. En <Tag>CABANA</Tag> escribí:{' '}
                  {invitadasDelRemate.map((i) => (
                    <span key={i.id_evento_invitado}>
                      {i.invitada} → {(i.cabanas || []).map((c) => <Tag key={c} color="blue">{c}</Tag>)}
                    </span>
                  ))}
                </Paragraph>
              )}
              <Paragraph style={{ marginBottom: 8 }}>
                La columna <Tag>CABANA</Tag> dice <strong>de qué cabaña es cada animal</strong>:
                el código corto o el nombre de la cabaña, el mismo que ves en
                «En el CSV escribí» de la pantalla de Cabañas. Dejala vacía si el
                animal es tuyo y tenés una sola cabaña.
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                La plantilla que descargás acá ya viene con una fila de ejemplo por
                cada cabaña invitada, y por cada una de las tuyas si tenés varias —
                copiá de ahí y no vas a errarle. Si un valor no coincide, esa fila
                <strong> se rechaza</strong>: nunca la cargamos a tu nombre por las dudas.
              </Paragraph>
            </div>
          }
        />
      )}
      {/* En la carga suelta la misma columna es OPCIONAL y elige entre MIS
          cabañas. Se explica solo si vale la pena: con una sola cabaña el
          backend la asigna solo y la columna es ruido; con varias, es la
          diferencia entre un animal que entra al catalogo y uno que no. Desde
          el 2026-10-26 la plantilla la trae sola en ese caso, con una fila de
          ejemplo por cabaña. */}
      {!idEvento && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="¿Tenés más de una cabaña?"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              La plantilla que descargás acá trae la columna <Tag>CABANA</Tag> con una
              fila de ejemplo por cada cabaña tuya: copiá de ahí lo que va en cada
              animal. Si tenés una sola, la columna no aparece y la cabaña se asigna
              sola. Un animal sin cabaña <strong>no entra al catálogo de genética</strong>.
            </Paragraph>
          }
        />
      )}

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          {/* Columna 1: Instrucciones, Campos requeridos, Formato de fecha */}
          <Col xs={24} md={12}>
            <Title level={4}>Instrucciones</Title>
            <Paragraph>
              <ol>
                <li>Descargue la plantilla CSV haciendo clic en el botón</li>
                <li>Complete los datos de los animales en Excel o similar</li>
                <li>Guarde el archivo manteniendo el formato CSV con separador <Tag>;</Tag> (punto y coma)</li>
                <li>Suba el archivo completado</li>
              </ol>
            </Paragraph>
            
            <Alert
              message="Campos requeridos"
              description={
                <span>
                  <Tag color="red">NOM_ANIMAL</Tag>
                  <Tag color="red">RP_ANIMAL</Tag>
                  <Tag color="red">FECHA_NAC</Tag>
                  <Tag color="red">REGISTRO</Tag>
                </span>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Alert
              message="Formato de fecha"
              description="Use el formato YYYY-MM-DD (ejemplo: 2023-01-15) o DD/MM/YYYY (ejemplo: 15/01/2023)"
              type="info"
              showIcon
            />
          </Col>

          {/* Columna 2: Listado de categorías */}
          <Col xs={24} md={12}>
            <Title level={4}>Categorías Disponibles</Title>
            
            {loadingCategorias ? (
              <Text type="secondary">Cargando categorías...</Text>
            ) : categorias.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <Table
                  scroll={{ x: 'max-content' }}
                  size="small"
                  dataSource={categorias}
                  rowKey="id_categoria"
                  pagination={false}
                  columns={[
                    { 
                      title: 'ID', 
                      dataIndex: 'id_categoria', 
                      key: 'id',
                      width: 60,
                      render: (id) => <Tag color="blue">{id}</Tag>
                    },
                    { 
                      title: 'Nombre', 
                      dataIndex: 'nom_categoria', 
                      key: 'nombre'
                    },
                  ]}
                  bordered
                />
              </div>
            ) : (
              <Alert
                message="No hay categorías disponibles"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            
            <Alert
              message={
                <span>
                  <InfoCircleOutlined /> Importante
                </span>
              }
              description="Recuerde asignar a sus animales el ID de la categoría correspondiente según la tabla anterior."
              type="warning"
              showIcon
            />
          </Col>
        </Row>
      </Card>

      {/* Zona de carga */}
      <Card title="Subir Archivo CSV" style={{ marginBottom: 24 }}>
        <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Haga clic o arrastre el archivo CSV a esta área
          </p>
          <p className="ant-upload-hint">
            Solo archivos .csv con separador punto y coma (;)
          </p>
        </Dragger>

        {file && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Archivo seleccionado: </Text>
            <Tag color="blue">{file.name}</Tag>
          </div>
        )}

        {uploading && (
          <Progress percent={uploadProgress} status="active" style={{ marginBottom: 16 }} />
        )}

        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={handleValidar}
            loading={validating}
            disabled={!file || uploading}
          >
            Validar Archivo
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleCargar}
            loading={uploading}
            disabled={!file || validating}
          >
            {uploading ? 'Cargando...' : 'Cargar Animales'}
          </Button>
        </Space>
      </Card>

      {/* Resultados de validación */}
      {resultadosValidacion && (
        <Card title="Resultado de Validación" style={{ marginBottom: 24 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Total Registros"
                value={resultadosValidacion.total}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Válidos"
                value={resultadosValidacion.total_validos}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Con Errores"
                value={resultadosValidacion.total_errores}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Col>
          </Row>

          {resultadosValidacion.total_errores > 0 && (
            <Collapse
              items={[{
                key: 'errores',
                label: <Text type="danger">Ver errores de validación ({resultadosValidacion.errores?.length})</Text>,
                children: (
                  <Table
                    scroll={{ x: 'max-content' }}
                    size="small"
                    dataSource={resultadosValidacion.errores}
                    rowKey="fila"
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
                      { title: 'RP', dataIndex: 'rp_animal', key: 'rp' },
                      { title: 'Nombre', dataIndex: 'nom_animal', key: 'nombre' },
                      {
                        title: 'Errores',
                        dataIndex: 'errores',
                        key: 'errores',
                        render: (errores) => (errores || []).map((e, i) => (
                          <Tag color="red" key={i}>{e}</Tag>
                        )),
                      },
                    ]}
                  />
                ),
              }]}
            />
          )}

          {resultadosValidacion.total_errores === 0 && (
            <Alert
              message="¡Archivo válido!"
              description="Todos los registros pasaron la validación. Puede proceder con la carga."
              type="success"
              showIcon
            />
          )}
        </Card>
      )}

      {/* Resultados de carga */}
      {resultadosCarga && (
        <Card title="Resultado de la Carga" style={{ marginBottom: 24 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Procesados"
                value={resultadosCarga.total_procesados}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Exitosos"
                value={resultadosCarga.total_exitosos}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Errores"
                value={resultadosCarga.total_errores}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Col>
          </Row>

          <Collapse
            ghost
            items={[
              ...(resultadosCarga.exitosos?.length > 0 ? [{
                key: 'exitosos',
                label: <Text type="success">Ver animales cargados ({resultadosCarga.exitosos?.length})</Text>,
                children: (
                  <Table
                    scroll={{ x: 'max-content' }}
                    size="small"
                    dataSource={resultadosCarga.exitosos}
                    rowKey="id_animal"
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
                      { title: 'RP', dataIndex: 'rp_animal', key: 'rp' },
                      { title: 'Nombre', dataIndex: 'nom_animal', key: 'nombre' },
                      { title: 'ID', dataIndex: 'id_animal', key: 'id' },
                    ]}
                  />
                ),
              }] : []),
              ...(resultadosCarga.errores?.length > 0 ? [{
                key: 'errores',
                label: <Text type="danger">Ver errores ({resultadosCarga.errores?.length})</Text>,
                children: (
                  <Table
                    scroll={{ x: 'max-content' }}
                    size="small"
                    dataSource={resultadosCarga.errores}
                    rowKey="fila"
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
                      { title: 'RP', dataIndex: 'rp_animal', key: 'rp' },
                      { title: 'Nombre', dataIndex: 'nom_animal', key: 'nombre' },
                      {
                        title: 'Error',
                        dataIndex: 'error',
                        key: 'error',
                        render: (text) => <Text type="danger">{text}</Text>,
                      },
                    ]}
                  />
                ),
              }] : []),
            ]}
          />

          {resultadosCarga.total_exitosos > 0 && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button type="primary" onClick={() => navigate('/animales')}>
                Ver Animales Cargados
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Modal de progreso. Se abre con la request y se cierra solo cuando el
          backend responde (uploading vuelve a false), venga bien o mal.
          El tiempo estimado sale de la cantidad de filas del CSV, contadas en
          el navegador antes de mandar nada. */}
      <Modal
        open={uploading}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width={460}
        centered
      >
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Spin
            size="large"
            indicator={<LoadingOutlined style={{ fontSize: 40, color: '#1890ff' }} spin />}
          />
          <Title level={4} style={{ marginTop: 20, marginBottom: 6 }}>
            Cargando animales
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
            {filasArchivo
              ? <>Procesando <strong>{filasArchivo}</strong> {filasArchivo === 1 ? 'fila' : 'filas'}. Deberia tardar unos <strong>{formatTiempo(segundosEstimados)}</strong>.</>
              : 'Procesando el archivo.'}
          </Text>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div style={{ marginBottom: 14 }}>
              <Progress percent={uploadProgress} size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>Subiendo archivo…</Text>
            </div>
          )}

          <Text strong style={{ display: 'block', marginBottom: 18, fontSize: 18, color: '#1890ff' }}>
            ⏱ {formatTiempo(tiempoTranscurrido)}
          </Text>

          {segundosEstimados && tiempoTranscurrido > segundosEstimados * 3 && (
            <Alert
              type="info"
              showIcon
              message="Esta tardando mas de lo previsto"
              description="Segui esperando: la carga puede seguir avanzando. Cuando termine vas a ver el detalle de cargados y con error."
              style={{ textAlign: 'left', marginBottom: 12 }}
            />
          )}

          <Alert
            type="warning"
            showIcon
            message="No cierres ni recargues esta pestaña"
            description="Si la cerrás, la carga se corta por la mitad y quedan animales sin cargar."
            style={{ textAlign: 'left' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CargaMasiva;
